import * as React from 'react'
import {
  useInvokeApi,
  usePageNotifications,
  useAuth,
  useSystemApi,
} from '../../hooks'
import { CurrentCustomerContext } from 'contexts'
import { Formik } from 'formik'
import { FormikSelect, EditModalActionBar } from '../../components'
import {
  Form,
  Box,
  Loader,
  List,
  ListItem,
  ListItemText,
  Divider,
} from 'cs-kevlar-react'
import { Option } from 'cs-kevlar-react/lib/es/Select'
import { Grid } from 'cs-kevlar-react'
import {
  DatabaseConfigurationViewModel,
  SupportUserPermission,
  ConnectorType,
  CustomerProductLine,
  NewCustomerAgencyShardMappingViewModel,
} from '../../api'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { useContext } from 'react'
import { AsyncPayload } from '../base'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { ListItemIcon } from '@mui/material'

interface EditDatabaseConfigurationProps {
  onClose?: () => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'hidden',
      overflowX: 'hidden',
      padding: theme.spacing(8),
    },
    icon: {
      minWidth: '30px',
    },
  }),
)

interface DatabaseConfigurationForm {
  currentShard: number
}

export const EditDatabaseConfiguration: React.FunctionComponent<EditDatabaseConfigurationProps> = ({
  onClose,
}) => {
  const classes = useStyles()
  const invokeApi = useInvokeApi()
  const { user: authUser } = useAuth()
  const { dispatch: dispatchNotification } = usePageNotifications()

  const {
    getDatabaseConfiguration,
    getShardMappingRulesForNewCustomers,
    getDefaultShardForNewCustomers,
    setDefaultShardForNewCustomers,
  } = useSystemApi()

  const [shardOptions, setShardOptions] = React.useState<
    AsyncPayload<Option[]>
  >({ payload: null, isLoading: true })

  const [
    systemDatabaseConfiguration,
    setSystemDatabaseConfiguration,
  ] = React.useState<AsyncPayload<DatabaseConfigurationViewModel>>({
    payload: null,
    isLoading: true,
  })

  const [shardMappingRules, setShardMappingRules] = React.useState<
    AsyncPayload<NewCustomerAgencyShardMappingViewModel[]>
  >({
    payload: null,
    isLoading: true,
  })

  const [currentShard, setCurrentShard] = React.useState<AsyncPayload<number>>({
    payload: null,
    isLoading: true,
  })

  if (authUser === null) return null

  const onEdit = (values: DatabaseConfigurationForm, actions: any) => {
    invokeApi(
      () => setDefaultShardForNewCustomers(values.currentShard),
      'There was an error updating the database configuration.',
      undefined,
      () => {
        dispatchNotification({
          type: 'SetSuccess',
          payload: `Database configuration.`,
        })

        if (onClose !== undefined) {
          onClose()
        }
      },
    )
  }

  React.useEffect(() => {
    const loadShardData = async () => {
      const response = await getDatabaseConfiguration()
      if (response.status === 200 && response.data) {
        setSystemDatabaseConfiguration({
          payload: response.data,
          isLoading: false,
        })

        setShardOptions({
          payload: response.data.shards.map(
            (shard) =>
              ({
                label: `${shard}`,
                key: `${shard}`,
                value: shard,
              } as Option),
          ),
          isLoading: false,
        })
      }
    }

    const loadCurrentShard = async () => {
      const response = await getDefaultShardForNewCustomers()
      if (response.status === 200) {
        setCurrentShard({
          payload: response.data,
          isLoading: false,
        })
      }
    }

    const loadShardMappingRules = async () => {
      const response = await getShardMappingRulesForNewCustomers()
      if (response.status == 200) {
        setShardMappingRules({
          payload: response.data,
          isLoading: false,
        })
      }
    }

    loadShardData()
    loadCurrentShard()
    loadShardMappingRules()
  }, [])

  const canEdit =
    authUser.isSupport &&
    authUser.permissions.includes(
      SupportUserPermission.ManageDatabaseConfiguration,
    )

  const isLoading =
    currentShard.isLoading ||
    systemDatabaseConfiguration.isLoading ||
    currentShard.isLoading ||
    shardMappingRules.isLoading

  return (
    <React.Fragment>
      {currentShard.payload !== null &&
        shardOptions.payload !== null &&
        systemDatabaseConfiguration.payload !== null &&
        shardMappingRules !== null && (
          <Formik
            initialValues={{ currentShard: currentShard.payload }}
            validationSchema={null}
            onSubmit={onEdit}
          >
            {(formikProps) => (
              <Form
                onSubmit={formikProps.handleSubmit}
                className={classes.form}
              >
                <Box className={classes.formFieldContainer}>
                  <Grid container direction="column">
                    <Grid item>
                      <FormikSelect
                        id="currentShard"
                        formikProps={formikProps}
                        label="Default New Customer Shard"
                        fieldName="currentShard"
                        options={shardOptions.payload as Option[]}
                        fullWidth
                      />
                    </Grid>
                    <Box className={classes.spacer} />
                  </Grid>
                </Box>

                <List dense={true}>
                  <ListItem>
                    <ListItemText
                      primary="Shard Mapping Rules"
                      secondary="New customers can map to shards based on rules.  If they don't match a rule they'll be mapped to the default."
                    />
                  </ListItem>
                  <Divider />

                  {(shardMappingRules.payload ?? []).map(
                    (x: NewCustomerAgencyShardMappingViewModel) => (
                      <List dense={true}>
                        <ListItem>
                          <ListItemText primary={`Shard ${x.shardId}`} />
                        </ListItem>

                        {x.connectorTypeGroups.map((connectorTypeGroup) => (
                          <ListItem>
                            <ListItemIcon className={classes.icon}>
                              <ChevronRightIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${
                                ConnectorType[connectorTypeGroup.connectorType]
                              }`}
                              secondary={`${connectorTypeGroup.productLines
                                .map((pl) => CustomerProductLine[pl])
                                .join(' \u00b7 ')}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    ),
                  )}
                </List>

                {
                  <EditModalActionBar
                    canDelete={false}
                    onCancel={onClose}
                    formikProps={formikProps}
                    isLoading={isLoading}
                    canSubmit={canEdit}
                    loader={
                      <Loader size="sm" text="Saving" disableVerticalGutters />
                    }
                  />
                }
              </Form>
            )}
          </Formik>
        )}
      {(currentShard.payload === null ||
        shardOptions.payload === null ||
        (systemDatabaseConfiguration.payload === null &&
          shardMappingRules.payload === null)) && (
        <Loader size="sm" text="Loading database configuration" />
      )}
    </React.Fragment>
  )
}
